/**
 * User-scoped browser storage.
 *
 * Prevents cross-user state leaks by scoping every localStorage /
 * sessionStorage key with the authenticated user's id. Without this,
 * User A's state (active analyses, timezone, etc.) leaks to User B
 * when they share a browser — which is exactly what happened in the
 * 2026-04-04 incident.
 *
 * Key format: `betterclose:<userId>:<logicalKey>`
 *
 * Usage:
 *   const storage = createUserScopedStorage(userId)
 *   storage.setJSON('active-analyses', data)
 *   const data = storage.getJSON<Analysis[]>('active-analyses', [])
 *
 * On sign-out, call `clearAllUserScopedStorage()` to wipe every scoped
 * entry (for every user id) plus the legacy unscoped keys from before
 * this refactor.
 */

const SCOPE_PREFIX = 'betterclose:'

/**
 * Legacy unscoped keys that existed before the 2026-04-04 scoping fix.
 * They are wiped on every sign-out so no pre-fix residue can leak.
 */
const LEGACY_LOCAL_KEYS = [
  'betterclose-active-analyses',
  'betterclose_timezone',
  'betterclose_time_format',
] as const

/**
 * Legacy sessionStorage keys set by Shop.tsx before Stripe checkout
 * redirect. These were never cleared on sign-out.
 */
const LEGACY_SESSION_KEYS = [
  'lastPurchaseType',
  'lastPurchaseName',
  'lastPurchaseCredits',
] as const

function buildKey(userId: string, key: string): string {
  return `${SCOPE_PREFIX}${userId}:${key}`
}

export interface UserScopedStorage {
  getJSON<T>(key: string, fallback: T): T
  setJSON<T>(key: string, value: T): void
  getString<T extends string>(key: string, fallback: T): T
  setString(key: string, value: string): void
  remove(key: string): void
  isReady: boolean
}

/**
 * Creates a user-scoped storage instance bound to a specific user id.
 *
 * If `userId` is null/undefined (e.g. user not yet loaded, signed out),
 * every method becomes a safe no-op and reads return the fallback.
 * This lets callers unconditionally call the storage without null checks.
 */
export function createUserScopedStorage(
  userId: string | null | undefined,
): UserScopedStorage {
  const isReady = Boolean(userId)

  return {
    isReady,

    getJSON<T>(key: string, fallback: T): T {
      if (!userId) return fallback
      try {
        const raw = localStorage.getItem(buildKey(userId, key))
        if (raw === null) return fallback
        return JSON.parse(raw) as T
      } catch {
        return fallback
      }
    },

    setJSON<T>(key: string, value: T): void {
      if (!userId) return
      try {
        localStorage.setItem(buildKey(userId, key), JSON.stringify(value))
      } catch {
        // quota exceeded / private browsing — drop silently
      }
    },

    getString<T extends string>(key: string, fallback: T): T {
      if (!userId) return fallback
      try {
        const raw = localStorage.getItem(buildKey(userId, key))
        return (raw as T) ?? fallback
      } catch {
        return fallback
      }
    },

    setString(key: string, value: string): void {
      if (!userId) return
      try {
        localStorage.setItem(buildKey(userId, key), value)
      } catch {
        // quota exceeded / private browsing — drop silently
      }
    },

    remove(key: string): void {
      if (!userId) return
      try {
        localStorage.removeItem(buildKey(userId, key))
      } catch {
        // no-op
      }
    },
  }
}

/**
 * Clears the sources of cross-user state leaks on sign-out:
 *   1. Legacy unscoped localStorage keys (pre-2026-04-04).
 *   2. Legacy sessionStorage checkout residue (Stripe redirect flow).
 *
 * Intentionally DOES NOT touch the `betterclose:<userId>:*` namespace.
 * That namespace is already isolated per user — the provider only ever
 * reads from `createUserScopedStorage(currentUserId)`, so a signed-in
 * User B cannot see User A's scoped data even if it survives in
 * localStorage. Wiping it on sign-out would destroy User A's own
 * in-progress analyses the next time they sign back in on the same
 * browser, which is exactly the UX regression we saw on 2026-04-05.
 *
 * Call this from `AuthContext.signOut()` and from the `SIGNED_OUT`
 * branch of `onAuthStateChange`.
 */
export function clearCrossUserLeakResidue(): void {
  // Wipe legacy unscoped localStorage keys (pre-2026-04-04 fix)
  for (const key of LEGACY_LOCAL_KEYS) {
    try {
      localStorage.removeItem(key)
    } catch {
      // no-op
    }
  }

  // Wipe legacy sessionStorage checkout residue
  for (const key of LEGACY_SESSION_KEYS) {
    try {
      sessionStorage.removeItem(key)
    } catch {
      // no-op
    }
  }
}
